// src/pages/team/TeamUserSettingsPage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  ErrorAlert,
} from '@react/components';
import { teamApi } from '@core/api';
import { getTeamName, refEntity } from '@/features/team/logic';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';
import { Select } from '@/components/Select';

function ToggleSwitch({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex items-center justify-between py-3">
      <div>
        <span className="text-sm font-medium text-foreground">{label}</span>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </label>
  );
}

export const TeamUserSettingsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTeam();
  const { t } = useI18n();

  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    notifications_enabled: true,
    email_notifications: true,
    push_notifications: true,
    display_mode: 'default',
  });

  useEffect(() => {
    if (!id) return;
    store.GetTeam(id);
    // teamApi directly: settings that were never saved come back as an error
    // from the service — that is the defaults case, not a page-level failure.
    teamApi.GetTeamUserSettings(id).then((res: any) => {
      const s = res?.settings ?? res;
      if (s) {
        setFormData({
          notifications_enabled: s.notifications_enabled ?? true,
          email_notifications: s.email_notifications ?? true,
          push_notifications: s.push_notifications ?? true,
          display_mode: s.display_mode ?? 'default',
        });
      }
    }).catch(() => { /* keep defaults */ });
    return () => { store.clearCurrent(); };
  }, [id]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleSave = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      await store.UpdateTeamUserSettings(id, formData as any);
      showSuccess(t('team.settingsSaved'));
    } catch { /* store handles */ } finally { setSubmitting(false); }
  };

  const handleReset = async () => {
    if (!id || !confirm(t('team.resetConfirm'))) return;
    try {
      await store.ResetTeamUserSettings(id, { team: refEntity(id) });
      setFormData({
        notifications_enabled: true,
        email_notifications: true,
        push_notifications: true,
        display_mode: 'default',
      });
      showSuccess(t('team.settingsReset'));
    } catch { /* store handles */ }
  };

  const teamName = getTeamName(store.currentItem) || t('team.title');

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('team.userSettings.breadcrumb')}</span>
      </nav>
      <TeamSectionNav teamId={id ?? ''} group="settings" active="my-settings" />


      <h1 className="text-2xl font-bold text-foreground">{t('team.userSettings.title')}</h1>
      <p className="text-sm text-muted-foreground">{t('team.userSettings.description')}</p>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {successMessage && <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      <Card>
        <CardHeader>
          <CardTitle>{t('team.userSettings.notifications')}</CardTitle>
          <CardDescription>{t('team.userSettings.notificationsDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <ToggleSwitch
            checked={formData.notifications_enabled}
            onChange={(v) => setFormData((p) => ({ ...p, notifications_enabled: v }))}
            label={t('team.userSettings.enableNotifications')}
            description={t('team.userSettings.enableNotificationsDesc')}
          />
          <ToggleSwitch
            checked={formData.email_notifications}
            onChange={(v) => setFormData((p) => ({ ...p, email_notifications: v }))}
            label={t('team.userSettings.emailNotifications')}
            description={t('team.userSettings.emailNotificationsDesc')}
          />
          <ToggleSwitch
            checked={formData.push_notifications}
            onChange={(v) => setFormData((p) => ({ ...p, push_notifications: v }))}
            label={t('team.userSettings.pushNotifications')}
            description={t('team.userSettings.pushNotificationsDesc')}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('team.userSettings.display')}</CardTitle>
          <CardDescription>{t('team.userSettings.displayDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-foreground">{t('team.userSettings.displayMode')}</label>
            <Select
              value={formData.display_mode}
              onChange={(e) => setFormData((p) => ({ ...p, display_mode: e.target.value }))} className="w-48">
              <option value="default">{t('team.userSettings.displayModeDefault')}</option>
              <option value="compact">{t('team.userSettings.displayModeCompact')}</option>
              <option value="comfortable">{t('team.userSettings.displayModeComfortable')}</option>
            </Select>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="ghost" onClick={handleReset}>{t('team.userSettings.resetToDefaults')}</Button>
          <Button onClick={handleSave} disabled={submitting}>
            {submitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
});

export default TeamUserSettingsPage;
